import { useEffect, useState } from "react";
import { X, Users, Percent, Save, Loader2, FileText, AlertCircle } from "lucide-react";
import api from "../lib/api";
import { fmtBRL } from "../lib/constants";
import { exportPartnershipReport } from "../lib/partnershipReport";

const DEFAULTS = {
  partnerAName: "Admin",
  partnerBName: "Kevin",
  partnerAShare: 50,           // % of services (partner A = admin/you)
  includePartsInSplit: false,  // if false → parts 100% to partner A
  splitExpenses: true,         // subtract expenses from services before splitting
};

export default function PartnershipModal({ periodLabel, orders, sales, expenses, onClose }) {
  const [cfg, setCfg] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    api.get("/settings").then((r) => {
      const p = r.data?.partnership || {};
      setCfg({ ...DEFAULTS, ...p });
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const saveConfig = async () => {
    setSaving(true);
    try {
      await api.put("/settings", { partnership: cfg });
      setToast({ ok: true, msg: "Configuração salva!" });
      setTimeout(() => setToast(null), 2200);
    } catch (e) {
      setToast({ ok: false, msg: e.response?.data?.detail || "Erro ao salvar." });
    } finally { setSaving(false); }
  };

  // ─── Compute the closure numbers ─────────────────────────────────
  const receitaServicos = orders.reduce((s, o) => s + (Number(o.total) || 0) - (Number(o.discount) || 0), 0);
  const custoServicos = orders.reduce((s, o) => s + (Number(o.costValue) || 0), 0);
  const receitaPecas = sales.reduce((s, x) => s + (Number(x.value) || 0) - (Number(x.discount) || 0), 0);
  const custoPecas = sales.reduce((s, x) => s + (Number(x.costValue) || 0), 0);
  const despesasTotal = expenses.reduce((s, e) => s + (Number(e.amount) || 0), 0);

  const shareA = Math.max(0, Math.min(100, Number(cfg.partnerAShare) || 0));
  const shareB = 100 - shareA;

  // Services net (revenue - direct cost - optionally - shared expenses)
  const servicosNet = receitaServicos - custoServicos - (cfg.splitExpenses ? despesasTotal : 0);
  const pecasNet = receitaPecas - custoPecas;

  const servicosA = (servicosNet * shareA) / 100;
  const servicosB = (servicosNet * shareB) / 100;

  const pecasA = cfg.includePartsInSplit ? (pecasNet * shareA) / 100 : pecasNet;
  const pecasB = cfg.includePartsInSplit ? (pecasNet * shareB) / 100 : 0;

  const totalA = servicosA + pecasA;
  const totalB = servicosB + pecasB;

  const generate = () => {
    exportPartnershipReport({
      periodLabel,
      cfg,
      orders,
      sales,
      expenses,
      calc: {
        receitaServicos, custoServicos, receitaPecas, custoPecas, despesasTotal,
        servicosNet, pecasNet,
        servicosA, servicosB, pecasA, pecasB, totalA, totalB,
        shareA, shareB,
      },
    });
  };

  if (loading) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] shadow-2xl overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-purple-700 to-purple-900 text-white flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Users className="w-5 h-5" />
            <div>
              <h3 className="font-heading font-bold text-base leading-tight">Fechamento entre sócios</h3>
              <p className="text-[11px] text-white/80 mt-0.5">Período: {periodLabel}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10" data-testid="partnership-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-auto p-6 space-y-5">
          {/* Config */}
          <section className="bg-slate-50 rounded-xl border border-slate-200 p-4 space-y-3">
            <h4 className="text-[11px] uppercase font-bold text-slate-500 tracking-wider">Configuração da sociedade</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] uppercase font-semibold text-slate-500">Nome do Sócio A (você)</label>
                <input value={cfg.partnerAName} onChange={(e) => setCfg({ ...cfg, partnerAName: e.target.value })}
                  data-testid="partner-a-name"
                  className="mt-1 w-full h-9 rounded-md border border-slate-300 px-3 text-sm focus:border-purple-500 focus:outline-none" />
              </div>
              <div>
                <label className="text-[11px] uppercase font-semibold text-slate-500">Nome do Sócio B</label>
                <input value={cfg.partnerBName} onChange={(e) => setCfg({ ...cfg, partnerBName: e.target.value })}
                  data-testid="partner-b-name"
                  className="mt-1 w-full h-9 rounded-md border border-slate-300 px-3 text-sm focus:border-purple-500 focus:outline-none" />
              </div>
            </div>

            <div>
              <label className="text-[11px] uppercase font-semibold text-slate-500">
                Participação do Sócio A nos serviços (%)
              </label>
              <div className="flex items-center gap-2 mt-1">
                <input type="range" min="0" max="100" step="5" value={shareA}
                  onChange={(e) => setCfg({ ...cfg, partnerAShare: parseInt(e.target.value) })}
                  className="flex-1"
                  data-testid="partner-a-share-range" />
                <div className="relative">
                  <input type="number" min="0" max="100" value={shareA}
                    onChange={(e) => setCfg({ ...cfg, partnerAShare: parseInt(e.target.value) || 0 })}
                    data-testid="partner-a-share"
                    className="w-20 h-9 rounded-md border border-slate-300 px-2 text-sm text-right focus:border-purple-500 focus:outline-none" />
                  <Percent className="w-3 h-3 absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                </div>
                <span className="text-xs text-slate-500 tabular-nums w-24">
                  {cfg.partnerBName}: {shareB}%
                </span>
              </div>
            </div>

            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={cfg.includePartsInSplit}
                  onChange={(e) => setCfg({ ...cfg, includePartsInSplit: e.target.checked })}
                  data-testid="include-parts"
                  className="w-4 h-4 accent-purple-600" />
                <span className="text-sm text-slate-700">
                  <b>Incluir vendas de peças</b> no rateio (padrão: peças ficam 100% com o Sócio A)
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={cfg.splitExpenses}
                  onChange={(e) => setCfg({ ...cfg, splitExpenses: e.target.checked })}
                  data-testid="split-expenses"
                  className="w-4 h-4 accent-purple-600" />
                <span className="text-sm text-slate-700">
                  <b>Descontar despesas fixas</b> dos serviços antes do rateio
                </span>
              </label>
            </div>

            <div className="flex justify-end">
              <button onClick={saveConfig} disabled={saving} data-testid="save-partnership-cfg"
                className="h-9 px-4 rounded-md text-xs font-semibold bg-slate-700 hover:bg-slate-800 text-white inline-flex items-center gap-1.5 disabled:opacity-50">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                Salvar como padrão
              </button>
            </div>
            {toast && (
              <div className={`text-xs ${toast.ok ? "text-emerald-700" : "text-rose-700"} inline-flex items-center gap-1.5`}>
                <AlertCircle className="w-3.5 h-3.5" /> {toast.msg}
              </div>
            )}
          </section>

          {/* Result */}
          <section>
            <h4 className="text-[11px] uppercase font-bold text-slate-500 tracking-wider mb-2">Rateio calculado</h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
              <div className="rounded-xl border-2 border-purple-200 bg-purple-50 p-4">
                <div className="text-[10px] uppercase font-bold text-purple-600 tracking-wider">
                  {cfg.partnerAName} (você)
                </div>
                <div className="font-heading text-3xl font-extrabold text-purple-900 tabular-nums mt-1" data-testid="partner-a-total">
                  R$ {fmtBRL(totalA)}
                </div>
                <div className="text-[11px] text-purple-700 mt-1 space-y-0.5">
                  <div>Serviços ({shareA}%): <b className="tabular-nums">R$ {fmtBRL(servicosA)}</b></div>
                  <div>Peças: <b className="tabular-nums">R$ {fmtBRL(pecasA)}</b> {cfg.includePartsInSplit ? `(${shareA}%)` : "(100%)"}</div>
                </div>
              </div>
              <div className="rounded-xl border-2 border-blue-200 bg-blue-50 p-4">
                <div className="text-[10px] uppercase font-bold text-blue-600 tracking-wider">
                  {cfg.partnerBName}
                </div>
                <div className="font-heading text-3xl font-extrabold text-blue-900 tabular-nums mt-1" data-testid="partner-b-total">
                  R$ {fmtBRL(totalB)}
                </div>
                <div className="text-[11px] text-blue-700 mt-1 space-y-0.5">
                  <div>Serviços ({shareB}%): <b className="tabular-nums">R$ {fmtBRL(servicosB)}</b></div>
                  <div>Peças: <b className="tabular-nums">R$ {fmtBRL(pecasB)}</b> {cfg.includePartsInSplit ? `(${shareB}%)` : "(0%)"}</div>
                </div>
              </div>
            </div>

            <div className="text-xs text-slate-600 space-y-1 border-t border-slate-100 pt-3">
              <div className="flex justify-between"><span>Receita bruta de serviços</span><b className="tabular-nums">R$ {fmtBRL(receitaServicos)}</b></div>
              <div className="flex justify-between"><span>(-) Custo de peças usadas nas OS</span><b className="tabular-nums text-rose-700">R$ {fmtBRL(custoServicos)}</b></div>
              {cfg.splitExpenses && (
                <div className="flex justify-between"><span>(-) Despesas fixas do período</span><b className="tabular-nums text-rose-700">R$ {fmtBRL(despesasTotal)}</b></div>
              )}
              <div className="flex justify-between border-t border-slate-100 pt-1"><span><b>Serviços líquido para rateio</b></span><b className="tabular-nums text-emerald-700">R$ {fmtBRL(servicosNet)}</b></div>
              <div className="flex justify-between mt-2"><span>Receita de peças</span><b className="tabular-nums">R$ {fmtBRL(receitaPecas)}</b></div>
              <div className="flex justify-between"><span>(-) Custo das peças vendidas</span><b className="tabular-nums text-rose-700">R$ {fmtBRL(custoPecas)}</b></div>
              <div className="flex justify-between"><span><b>Peças líquido</b></span><b className="tabular-nums text-emerald-700">R$ {fmtBRL(pecasNet)}</b></div>
            </div>
          </section>
        </div>

        <div className="px-6 py-3 border-t border-slate-100 bg-slate-50 flex justify-end gap-2">
          <button onClick={onClose} className="h-10 px-4 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-200">
            Fechar
          </button>
          <button
            onClick={generate}
            data-testid="generate-partnership-report"
            className="h-10 px-5 rounded-lg text-sm font-semibold bg-purple-600 hover:bg-purple-700 text-white inline-flex items-center gap-2"
          >
            <FileText className="w-4 h-4" />
            Gerar relatório completo
          </button>
        </div>
      </div>
    </div>
  );
}
